import {
	Box,
	Button,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalOverlay,
	Skeleton,
	VStack,
} from '@chakra-ui/react';
import { ReactNode } from 'react';
import { spacing } from 'theme/tokens';
import ModalHeaderAccent from './modal-header-accent';

interface FormModalProps {
	/** Whether the modal is open */
	isOpen: boolean;
	/** Close callback */
	onClose: () => void;
	/** Modal title */
	title: string;
	/** Form body content */
	children: ReactNode;
	/** Primary submit handler */
	onSubmit: () => void;
	/** Submit button label (default "Save") */
	submitLabel?: string;
	/** Cancel button label (default "Cancel") */
	cancelLabel?: string;
	/** Disables the submit button */
	isSubmitDisabled?: boolean;
	/** Shows a spinner on the submit button */
	isSubmitting?: boolean;
	/** Optional destructive action (e.g. delete) — rendered on the left side of the footer */
	destructiveAction?: {
		label: string;
		onClick: () => void;
		isLoading?: boolean;
	};
	/** Color accent for the header bar */
	colorScheme?: 'brand' | 'accent' | 'danger' | 'warning';
	/**
	 * Modal width. Convention:
	 *   - "md"  → 1-3 fields (simple forms, confirmations)
	 *   - "lg"  → 4-6 fields (standard forms)
	 *   - "xl"  → 7-10 fields (complex forms)
	 *   - "2xl" → 10+ fields or multi-section layouts
	 */
	size?: string;
	/** Show skeleton loading state instead of children */
	isLoading?: boolean;
	/** Number of skeleton fields to show (default 4) */
	skeletonFieldCount?: number;
}

const FOOTER_BTN_HEIGHT = '44px';

export default function FormModal({
	isOpen,
	onClose,
	title,
	children,
	onSubmit,
	submitLabel = 'Save',
	cancelLabel = 'Cancel',
	isSubmitDisabled,
	isSubmitting,
	destructiveAction,
	colorScheme = 'brand',
	size = 'md',
	isLoading,
	skeletonFieldCount,
}: FormModalProps) {
	return (
		<Modal isOpen={isOpen} onClose={onClose} size={size} motionPreset="scale">
			<ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
			<ModalContent borderRadius="16px" overflow="hidden">
				<ModalHeaderAccent colorScheme={colorScheme}>{title}</ModalHeaderAccent>
				<ModalCloseButton />

				<ModalBody pb={4}>
					{isLoading ? (
						<VStack spacing={spacing.fieldGap} align="stretch" py={2}>
							{Array.from({ length: skeletonFieldCount ?? 4 }).map((_, i) => (
								<Box key={i}>
									<Skeleton height="12px" width="80px" mb="6px" borderRadius="4px" />
									<Skeleton height="40px" borderRadius="16px" />
								</Box>
							))}
						</VStack>
					) : (
						children
					)}
				</ModalBody>

				<ModalFooter pt={0}>
					<Flex w="full" justify="space-between" align="center">
						{/* Left: destructive action */}
						<Box>
							{destructiveAction && (
								<Button
									variant="modalCtaDanger"
									w="auto"
									h={FOOTER_BTN_HEIGHT}
									isLoading={destructiveAction.isLoading}
									onClick={destructiveAction.onClick}
								>
									{destructiveAction.label}
								</Button>
							)}
						</Box>

						{/* Right: cancel + submit */}
						<Flex gap={3} align="center">
							<Button variant="ghost" h={FOOTER_BTN_HEIGHT} onClick={onClose}>
								{cancelLabel}
							</Button>
							<Button
								variant="modalCta"
								w="auto"
								h={FOOTER_BTN_HEIGHT}
								isLoading={isSubmitting}
								isDisabled={isSubmitDisabled || isLoading}
								onClick={onSubmit}
							>
								{submitLabel}
							</Button>
						</Flex>
					</Flex>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
